// Re-exported for use by HotelPDP3 without directly coupling to HotelPDPPageV2
export type { TravelOffer, TravelOfferUnitComponent } from './EventAccommodations/routes/HotelPDPPageV2/HotelPDPPageV2.types';

export type Property = {
  address: string | null;
  id: string;
  images: Array<{ src: string; alt: string }>;
  displayName: string | null;
  rating: number | null;
  description: string | null;
  location: { lat: number; lon: number } | null;
  highlightedAmenities: Array<{ display: string; id: string }>;
  amenities: Array<{ display: string; id: string }>;
};

export type Unit = {
  id: string;
  images: Array<{ src: string; alt: string }>;
  displayName: string;
  rating: number | null;
  size: string | null;
  sleepsNumber: number;
  beds: string;
  displayRate: string;
  amenities: Array<{ display: string; id: string }>;
};

export type StaysSearchTerms = {
  destination: string;
  lat: number;
  lng: number;
  googlePlaceId?: string;
  startDate: {
    year: number;
    month: number;
    day: number;
  };
  endDate: {
    year: number;
    month: number;
    day: number;
  };
  rooms: Array<{
    adults: number;
    childrenAges: Array<number>;
  }>;
  loading?: boolean;
};

/**
 * Property identifier types for accommodations
 * Extends the GraphQL AccommodationCatalogPropertyIdentifierV2Type to include accommodationPlace
 */
export enum PropertyIdentifierType {
  JoyPlace = 'joyPlace',
  AccommodationPlace = 'accommodationPlace',
}

/**
 * Consolidated property identifier type
 */
export type PropertyIdentifier = {
  readonly id: string;
  readonly type: PropertyIdentifierType;
};

/**
 * Telemetry load outcome for timing instrumentation events
 */
export type LoadStatus = 'success' | 'empty' | 'error';

/**
 * Pricing API call source identifier.
 * Names the page that makes a pricing request, so travel_service can split its funnel by page.
 *
 * Keep the values flat — one value per page, never a `context_page` combination. A new guest page
 * needs a new value here and a new line in `CALL_SOURCE_DESCRIPTION` in travel_service. It needs no
 * schema change in either repo, because `callSource` is a nullable `String`.
 */
export enum PricingApiCallSource {
  GuestMap = 'guest_map',
  AdminMap = 'admin_map',
  GuestAccommodation = 'guest_accommodation',
  AdminAccommodation = 'admin_accommodation',
  /** The hotel search console at `/:eventHandle/hotel/search`. */
  GuestHotelSearch = 'guest_hotel_search',
  /**
   * The redirect step at `/:eventHandle/hotel/:joyPlaceId`. It resolves one redirect for one joyPlace,
   * then sends the guest onward. A guest never reads this page, so the value says `redirect`, not
   * `detail`. The detail page they land on is a different surface and needs its own value.
   */
  GuestHotelPlaceRedirect = 'guest_hotel_place_redirect',
  /** The redirect step at `/:eventHandle/accommodation-place/:accommodationPlaceId`. */
  GuestAccommodationPlaceRedirect = 'guest_accommodation_place_redirect',
  /** The hotel detail page at `/stays/:joyPlaceId`, which is shareable and carries no event handle. */
  GuestHotelPlaceDetail = 'guest_hotel_place_detail',
  /** The hotel detail page at `/:eventHandle/accommodations/:accommodationPlaceId`, inside the event site. */
  GuestAccommodationPlaceDetail = 'guest_accommodation_place_detail',
  /**
   * The hotel detail page at `/travel/:placeId`. Not prefixed `guest_`, because Public Stays has no
   * event and no couple. A guest there arrived without a wedding website.
   */
  PublicStaysDetail = 'public_stays_detail',
}
